import classNames from 'classnames';
import Link from 'next/link';
import React, {ReactElement, useCallback, useMemo, useState} from 'react';

import {MoneyIcon, ProductionIcon} from '../../../resources/Icons';
import Col from '../../common/Col';
import {useEnvironment} from '../../common/EnvironmentProvider';
import Row from '../../common/Row';
import {GameScore} from '../../query/graphql';
import {formatNumber} from '../../tool/decision/util';
import {useGameScores} from '../hooks/useGameScores';
import {formatMoney} from '../mechanics';
import {LeaderIconId, LEADERS} from '../resources/leaderIcons';
import ArrowButton from './buttons/ArrowButton';
import NormalButton from './buttons/NormalButton';
import {PillButton} from './buttons/PillButtons';
import DotsLoader from './DotsLoader';
import MessageBox from './MessageBox';
import {SimplePill} from './SimplePill';

type Props = {
  myScore: GameScore | undefined;
  initialEntries?: GameScore[];
  onClose: () => void;
  onPlayAgain: () => void;
};

// This is just use to reserve space while loading data
const dummyEntries: GameScore[] = Array(6)
  .fill(null)
  .map((_, index) => ({
    uuid: `-${index}`,
    position: -index,
    name: 'AAA',
    iconId: 'ro',
    money: 0,
    production: 0,
    isFirst: false,
    isLast: false,
  }));

const LeaderboardList = ({myScore, initialEntries, onClose, onPlayAgain}: Props): ReactElement | null => {
  const environment = useEnvironment();
  const [sortByMoney, setSortByMoney] = useState(false);
  const [page, setPage] = useState(0);
  const {
    data: entries,
    isLoading,
    error,
  } = useGameScores({myEntryId: myScore?.uuid ?? null, page, sortByMoney}, initialEntries);
  const MyIcon = myScore ? LEADERS[myScore.iconId as LeaderIconId] : undefined;

  const handleClickPrev = useCallback(() => {
    setPage(prevState => prevState - 1);
  }, []);

  const handleClickNext = useCallback(() => {
    setPage(prevState => prevState + 1);
  }, []);

  const handleClickProduction = useCallback(() => {
    setPage(0);
    setSortByMoney(false);
  }, []);

  const handleClickMoney = useCallback(() => {
    setPage(0);
    setSortByMoney(true);
  }, []);

  const bottomButton = useMemo(() => {
    if (!myScore) {
      return <div className="flex justify-center">{<NormalButton onClick={onClose}>Close</NormalButton>}</div>;
    } else {
      return (
        <Row className="justify-between px-28 items-center gap-xs">
          <Link href={environment?.game.feedbackFormUrl ?? ''} target="_blank" rel="noopener noreferrer">
            <NormalButton>Provide game feedback</NormalButton>
          </Link>
          <ArrowButton direction="right" onClick={onPlayAgain}>
            Play again
          </ArrowButton>
        </Row>
      );
    }
  }, [environment?.game.feedbackFormUrl, myScore, onClose, onPlayAgain]);

  const topButton = useMemo(
    () => (
      <SimplePill className={classNames(myScore && '!pr-xs')}>
        <Row className={'items-center gap-md'}>
          Leaderboard
          {!!myScore && <span className="text-base text-primary-800">{`${myScore ? 2 : 1}/2`}</span>}
        </Row>
      </SimplePill>
    ),
    [myScore]
  );

  return (
    <MessageBox
      dark
      top={topButton}
      bottom={bottomButton}
      midLeft={
        <ArrowButton
          direction="left"
          disabled={isLoading || entries?.[0]?.isFirst}
          onClick={handleClickPrev}
          aria-label={'Previous page'}
        />
      }
      midRight={
        <ArrowButton
          direction="right"
          disabled={isLoading || entries?.[entries.length - 1]?.isLast}
          onClick={handleClickNext}
          aria-label={'Next page'}
        />
      }
    >
      <Col className={'mt-xs gap-md'}>
        <Row className={'justify-center gap-sm'}>
          <PillButton
            disabled={isLoading}
            icon={<ProductionIcon aria-hidden />}
            className={classNames('!text-base overflow-hidden', !isLoading && !sortByMoney && '!bg-primary-600')}
            onClick={handleClickProduction}
          >
            Production
          </PillButton>
          <PillButton
            disabled={isLoading}
            icon={<MoneyIcon className="h-[45px] w-[45px]" aria-hidden />}
            className={classNames('!text-base overflow-hidden', !isLoading && sortByMoney && '!bg-primary-600')}
            onClick={handleClickMoney}
          >
            Earning
          </PillButton>
        </Row>
        <div className={'relative'}>
          <ul
            className={classNames(
              'border-[0.4rem] border-primary-300 rounded-[3rem] bg-white p-md text-primary-600',
              'grid grid-cols-2 mobile:grid-cols-1 gap-xs overflow-auto',
              !entries && 'opacity-0'
            )}
          >
            {!error &&
              (entries ?? dummyEntries).map(entry => {
                const Icon = LEADERS[entry.iconId as LeaderIconId] as typeof LEADERS[LeaderIconId] | undefined;
                const itsMe = entry.uuid === myScore?.uuid;

                return (
                  <li
                    key={`${entry.uuid}`}
                    className={'mobile:min-w-fit flex flex-row gap-[1px] items-stretch font-medium'}
                  >
                    <SimplePill
                      variant={itsMe ? 'secondary' : 'primary'}
                      className={'flex-none min-w-[7.1rem] min-h-[7.1rem] z-[1] !m-0 !px-sm !py-0 !text-lg'}
                    >
                      {entry.position}
                    </SimplePill>
                    <SimplePill
                      variant={itsMe ? 'secondary' : 'primary'}
                      className={'grow !pl-0 !pr-xs !py-0 !flex-row gap-4 justify-between !text-lg'}
                    >
                      {Icon ? (
                        <Icon
                          className={classNames(
                            'flex-none h-[7rem] w-[7rem] -mx-1 border-normal rounded-full',
                            itsMe ? 'bg-primary-300 border-primary-300' : 'bg-gray-100 border-gray-100'
                          )}
                        />
                      ) : (
                        <div
                          className={classNames(
                            'flex-none h-[7rem] w-[7rem] -mx-1 border-normal rounded-full',
                            'bg-gray-100 border-gray-100'
                          )}
                        ></div>
                      )}
                      <span className={'grow'}>{entry.name}</span>
                      <Row
                        className={classNames('gap-4 items-center', itsMe ? 'text-primary-300' : 'text-primary-600')}
                      >
                        {sortByMoney ? (
                          <>
                            <MoneyIcon className="h-[3.5rem] w-[3.5rem]" />
                            {formatMoney(entry.money)}
                          </>
                        ) : (
                          <Row className={'gap-4 whitespace-nowrap'}>
                            <ProductionIcon className={'h-[3.5rem] w-[3.5rem]'} role="img" />
                            {formatNumber(entry.production, 0, 0)} tonnes
                          </Row>
                        )}
                      </Row>
                    </SimplePill>
                  </li>
                );
              })}
          </ul>

          {isLoading && (
            <Col className={'absolute inset-0 items-center justify-center'}>
              <DotsLoader />
            </Col>
          )}
          {!!error && (
            <Col className={'absolute inset-0 items-center justify-center'}>
              <span className={'text-lg text-red-200'}>{String(error)}</span>
            </Col>
          )}
        </div>
        {myScore && (
          <Row
            className={'max-w-full mx-auto overflow-auto items-center gap-md text-lg font-semibold whitespace-nowrap'}
          >
            <span>Your score: </span>
            <Row className={'items-center gap-4'}>
              {MyIcon && (
                <MyIcon className={'w-[7rem] h-[7rem] border-normal rounded-full bg-gray-100 border-gray-100'} />
              )}{' '}
              {myScore.name}
            </Row>
            <Row className={'items-center gap-4 text-primary-300'}>
              <ProductionIcon className={'h-[3.5rem] w-[3.5rem]'} />
              {formatNumber(myScore.production, 0, 0)} tonnes
            </Row>
            <Row className={'items-center gap-4 text-primary-300'}>
              <MoneyIcon className="h-[3.5rem] w-[3.5rem]" />
              {formatMoney(myScore.money)}
            </Row>{' '}
          </Row>
        )}
      </Col>
    </MessageBox>
  );
};

export default LeaderboardList;
